import { stepPose } from './animation'
/**
 * Visual resolution: game state in, "what should this look like" out.
 *
 * This is deliberately free of both Canvas and WebGL. The renderer used to own
 * this logic, which meant deciding *what* to draw was welded to knowing *how*,
 * and the interesting half -- the springs that latch, the flags that rise, the
 * hazard that spins, the fire Tavi palette cycle -- could not be tested
 * without a rendering context. Now the decisions live here as plain functions
 * over plain data, and `Renderer3D` and the canvas HUD are both just consumers.
 *
 * Two invariants worth preserving, because both are easy to break silently:
 *
 *   - A resolved `Visual` names a sprite key that exists. `null` means "draw
 *     nothing", and it is a real answer (a collected block, a hidden block
 *     before it is bumped), not a failure.
 *   - Positions here are world-space pixels in the same coordinate system the
 *     simulation uses, Y down. Nothing here rounds to a screen pixel; that
 *     used to matter for Canvas integer blitting and no longer does, but the
 *     3D renderer still wants the same anchor semantics: entities are centred
 *     on their hitbox, objects and the player are planted on their base.
 */

import { getSprite } from './atlas'
import type { Entity } from './entity'
import type { Level, PlacedObject } from '../game/level'
import { Solidity, Terrain } from '../game/types'

// ── maths helpers ────────────────────────────────────────────────────────────

/** Read a finite number out of a `Record<string, unknown>` state bag. */
export function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/** Read a finite number, or a fallback. */
export function toInt(v: unknown, fallback = 0): number {
  const n = num(v)
  return n === undefined ? fallback : Math.floor(n)
}

/** Clamp to 0..1. */
export function unit(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

// ── visual descriptors ───────────────────────────────────────────────────────

/** How something should be drawn, resolved from game state alone. */
export interface Visual {
  key: string
  alpha: number
  /** Pixel offset from the anchor, e.g. a raised flag or a bobbing hazard. */
  dx: number
  dy: number
  /** Radians about the sprite centre; 0 means no rotation. */
  rot: number
  /** Uniform scale, 1 = native size. */
  scale: number
  /** Tint colour, or null to draw the sprite untouched. */
  tint: string | null
}

export function visual(key: string, over: Partial<Visual> = {}): Visual {
  return {
    key,
    alpha: 1,
    dx: 0,
    dy: 0,
    rot: 0,
    scale: 1,
    tint: null,
    ...over,
  }
}

/** Terrain tile to sprite key, or null for air. */
export function terrainKey(tile: number, above: number): string | null {
  switch (tile) {
    case Terrain.GROUND:
      return above === Terrain.GROUND ? 'ground' : 'ground-top'
    case Terrain.HARD:
      return 'hard-block'
    case Terrain.BRICK:
      return 'brick'
    case Terrain.PLATFORM:
      return 'platform'
    case Terrain.SLOPE_L:
      return 'slope-left'
    case Terrain.SLOPE_R:
      return 'slope-right'
    default:
      return null
  }
}

// ── placed objects ───────────────────────────────────────────────────────────

/**
 * A placed object's *identity*, ignoring which pixel-art frame it would use.
 *
 * The coin is the case that matters. It used to cycle `coin` / `coin-frame2` /
 * `coin-frame3` to fake a spin, and the 3D renderer now draws a real solid of
 * revolution instead. Resolving to a single key is what stops the renderer
 * alternating between a 3D coin and a flat pixel-art frame -- which is precisely
 * what it was doing, and precisely why the coins looked like frame-by-frame
 * pixel art with a 3D flicker every third of a second.
 *
 * The frames stay in the atlas and in the model table: they are still valid art,
 * and mapping them costs one line each. The resolver just no longer asks for
 * them.
 */
const COIN_FAMILY = new Set(['coin', 'coin-frame2', 'coin-frame3'])

/** The canonical key for anything in the coin family, or the key unchanged. */
function canonicalKey(key: string): string {
  return COIN_FAMILY.has(key) ? 'coin' : key
}

/**
 * How a single object should be drawn, where the *view* changes the answer.
 */
export interface ObjectVisualOptions {
  /**
   * Draw a hidden block whether or not it has been hit.
   *
   * The editor sets this: a hidden block is a gameplay secret, and a course
   * builder cannot place what it cannot see. Play leaves it off, which is what
   * makes the block hidden.
   */
  revealHidden?: boolean
}

/** Resolve an object's sprite and animation from its state, or null to skip. */
export function objectVisual(
  o: PlacedObject,
  level: Level,
  opts: ObjectVisualOptions = {},
): Visual | null {
  const id = o.def.id
  const st = o.state
  const tick = level.tick
  if (st.collected === true) return null
  if (
    id === 'hidden-cache' &&
    st.revealed !== true &&
    opts.revealHidden !== true
  )
    return null
  if (id === 'onoff-block' && level.onOffActive !== true) return null

  // Behaviours may publish their own clock as `phase`; fall back to the level
  // tick so the object still animates when it does not.
  const phase = num(st.phase) ?? tick / 60

  let v: Visual
  switch (id) {
    case 'crate':
    case 'crate-expansion-cell':
    case 'crate-ember-cell':
    case 'crate-prism':
      v = visual(st.spent === true ? 'crate-block-used' : 'crate-block')
      break
    case 'hidden-cache':
      // In play this is only reached once the block has been hit, so it looks
      // used. The editor asks for it unhit too (`revealHidden`), and then it is
      // its own ghost sprite — a solid crate would read as a block the player
      // can see, which is the one thing this object is not.
      v = visual(st.revealed === true ? 'crate-block-used' : 'hidden-cache')
      break
    case 'onoff':
    case 'switch':
      v = visual(st.on === false ? 'onoff-block-off' : 'onoff-block')
      if (id === 'switch' && st.pressed !== true) v.alpha = 0.5
      break
    case 'spring':
      v = visual(st.compress === true ? 'spring-extended' : 'spring')
      break
    case 'coin':
      // No frame cycling -- see `canonicalKey`. The 3D model spins; a sprite
      // swap would replace it with flat art every third of a second.
      v = visual('coin')
      break
    case 'cannon':
      v = visual(
        st.firing === true || st.fire === true ? 'cannon-fire' : 'cannon',
      )
      break
    case 'saw':
    case 'saw-h':
      v = visual('saw', { rot: num(st.spin) ?? tick / 24 })
      break
    case 'lava':
      v = visual('lava-top', { dy: Math.round(Math.sin(phase * Math.PI) * 1) })
      break
    // `fire-bar`, `water-top` and `spike-ball` used to be animated here. The
    // sprites are still authored in `art/tiles.ts`, but no object def declares
    // those ids, so the cases were unreachable -- which is worse than absent:
    // they read as "these objects exist and animate", and the next person to
    // register a def with one of those ids would get animation nobody had
    // tested. The art stays; the branches go.
    case 'checkpoint':
      // Raising the flag is a 8px lift: there is only one flag sprite, and
      // the pole below it stays put, so the flag visibly runs up the pole.
      v = visual('checkpoint-flag', { dy: st.taken === true ? -8 : 0 })
      break
    case 'goal':
      v = visual('goal-flag', { dy: st.taken === true ? -8 : 0 })
      break
    default:
      v = visual(o.def.sprite)
      break
  }

  // A scenery object with no art is a placeholder, not something to shout
  // about: skip it rather than stamping magenta over the level.
  if (getSprite(v.key) === undefined && o.def.solidity === Solidity.NONE)
    return null
  v.key = canonicalKey(v.key)
  return v
}

// ── entities ─────────────────────────────────────────────────────────────────

/** Resolve an entity's sprite from its kind and per-instance data. */
export function entityVisual(e: Entity, tick: number): Visual | null {
  const d = e.data
  const anim = d.anim
  const phase = num(d.phase) ?? tick / 60

  switch (e.kind) {
    case 'rivet':
      if (d.mode === 'squash') return visual('rivet-flat')
      return visual(`rivet-walk${stepPose(num(anim) ?? tick / 8)}`)
    case 'rivet-squash':
      return visual('rivet-flat')
    case 'scout':
    case 'wanderer':
      if (d.mode === 'shell') return visual('scout-wheel')
      return visual(`scout-walk${stepPose(num(anim) ?? tick / 8)}`)
    case 'scout-wheel':
      return visual('scout-wheel')
    case 'shardling':
      return visual(`shardling-walk${stepPose(num(anim) ?? tick / 8)}`)
    case 'wisp':
      return visual('wisp')
    case 'expansion-cell':
      return visual('expansion-cell')
    case 'ember-cell':
      return visual('ember-cell')
    case 'prism':
      return visual('prism', { rot: Math.sin(phase * Math.PI * 2) * 0.15 })
    case 'spare-battery':
      return visual('spare-battery')
    case 'coin':
      // No frame cycling: a 3D coin is one object that turns, and asking for a
      // different sprite per frame would swap the model out from under it.
      return visual('coin')
    case 'coin-pop': {
      // A popped coin spins like a placed one but rises as its life runs out.
      const life = unit(num(d.life) ?? 0)
      return visual('coin-pop', { dy: -life * 32 })
    }
    case 'fireball':
      return visual('fireball', { tint: '#f87858' })
    case 'debris':
      return visual('brick', { scale: 0.5 })
    case 'saw':
    case 'saw-h':
      return visual('saw', { rot: num(d.spin) ?? tick / 24 })
    case 'moving-platform':
    case 'moving-platform-h':
      return visual('moving-platform')
    default:
      return null
  }
}

// ── the player, as far as presentation is concerned ──────────────────────────

/**
 * `Level.player` is typed as the minimal `PlayerLike`, but resolving the
 * animation needs the full surface. Every field past `alive` is optional so a
 * bare stub player still resolves to a standing Tavi instead of throwing.
 */
export interface PlayerView {
  x: number
  y: number
  w: number
  h: number
  facing: number
  alive: boolean
  animState?: string
  state?: string
  power?: string
  isShielded?: boolean
  shieldTime?: number
  invuln?: number
  move?: { animTimer?: number; squash?: number }
}

/** Shield power flashes through these four colours, three ticks each. */
export const SHIELD_FLASH = [
  '#fcd800',
  '#f8b800',
  '#e8e8e8',
  '#fcfcfc',
] as const
/** Fire Tavi alternates red and white. */
export const FIRE_FLASH = ['#fcfcfc', '#d82800'] as const

export interface ResolvedPlayer {
  visual: Visual
  /** False while a post-hit blink is suppressing the sprite. */
  visible: boolean
  /** Vertical squash about the feet, 1 = none. */
  squash: number
}

/** The sprite key for the player's current pose. */
export function playerKey(p: PlayerView): string {
  const power = p.state ?? p.power ?? 'small'
  const base = power === 'small' ? 'small' : 'big'
  const anim = p.animState ?? 'idle'
  const animTimer = p.move?.animTimer ?? 0

  if (anim === 'walk' || anim === 'run') {
    return `tavi-${base}-${anim}${stepPose(animTimer)}`
  }
  if (anim === 'jump') return `tavi-${base}-jump`
  if (anim === 'skid') return `tavi-${base}-skid`
  if (anim === 'duck') return 'tavi-big-duck'
  // 'idle' and 'climb' share the standing pose.
  return `tavi-${base}-idle`
}

/** Resolve the player's pose, palette cycle and landing squash from state. */
export function playerVisual(
  p: PlayerView,
  tick: number,
): ResolvedPlayer | null {
  if (p.alive === false) return null

  const key = playerKey(p)
  const prism = p.isShielded === true || (p.shieldTime ?? 0) > 0
  // Post-hit invulnerability blinks the whole sprite.
  const visible =
    prism || (p.invuln ?? 0) <= 0 || Math.floor(tick / 2) % 2 === 0
  if (!visible) return null

  let v = visual(key)
  if (p.state === 'fire') {
    const cycle =
      FIRE_FLASH[Math.floor(tick / 5) % FIRE_FLASH.length] ?? '#fcfcfc'
    v = visual(key, { tint: cycle })
  } else if (prism) {
    const cycle =
      SHIELD_FLASH[Math.floor(tick / 3) % SHIELD_FLASH.length] ?? '#fcd800'
    v = visual(key, { tint: cycle })
  }

  // Quantised to 1/16 so the art stays legible while it is being squashed.
  const raw = p.move?.squash ?? 0
  const squash =
    raw > 0 ? Math.max(0.5, Math.round((1 - raw / 40) * 16) / 16) : 1
  return { visual: v, visible, squash }
}
